// src/components/UserProfileModal.js
import React, { useState, useEffect } from "react";
import supabase from "../../../supabaseClient";

const UserProfileModal = ({ user, isOpen, onClose }) => {
  const [isEditable, setIsEditable] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [uid, setUid] = useState('');
  const [verifiedStatus, setVerifiedStatus] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (user) {
      setDisplayName(user.displayName || '');
      setPhoneNumber(user.phoneNumber || '');
      // Email is immutable; no need to set it here for editing
      setUid(user.uid || '');
      setVerifiedStatus(user.verifiedStatus || false);
    }
  }, [user]);

  const handleEditClick = async () => {
    if (isEditable) {
      // Save the profile changes to Supabase when the form is editable
      try {
        const updates = {
          // Email is excluded to keep it immutable
          data: {
            full_name: displayName,
            phone: phoneNumber,
          },
        };

        const { data, error } = await supabase.auth.updateUser(updates);

        if (error) {
          console.error("Supabase Error:", error);
          setError(error.message);
        } else {
          setSuccess("Profile updated successfully!");
          setIsEditable(false);
        }
      } catch (error) {
        console.error("Unexpected Error:", error);
        setError("Failed to update profile.");
      }
    } else {
      setIsEditable(true);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    switch (name) {
      case 'displayName':
        setDisplayName(value);
        break;
      case 'phoneNumber':
        setPhoneNumber(value);
        break;
      default:
        break;
    }
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 overflow-y-auto h-full w-full flex justify-center items-center z-50">
      <div className="relative p-4 bg-white rounded-lg shadow-lg max-w-3xl w-full mx-4 md:mx-auto">
        <button onClick={onClose} className="absolute top-2 right-2 text-gray-600 hover:text-gray-900 focus:outline-none">
          <span className="text-2xl">&times;</span>
        </button>
        <h1 className="text-xl text-center font-bold mb-4 text-black">User Profile</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputField label="Name" name="displayName" value={displayName} onChange={handleInputChange} disabled={!isEditable} />
          <InputField label="Phone" name="phoneNumber" value={phoneNumber} onChange={handleInputChange} disabled={!isEditable} />
          <ReadOnlyField label="Email Address" value={user.email} />
          <ReadOnlyField label="User ID" value={uid} />
        </div>
        {error && <p className="text-red-500 text-xs italic text-center mt-2">{error}</p>}
        {success && <p className="text-green-500 text-xs italic text-center mt-2">{success}</p>}
        <div className="text-center">
          <button
            className="bg-yellow-400 text-center text-black py-2 px-4 flex items-center justify-center rounded hover:bg-yellow-700 focus:outline-none focus:ring focus:border-yellow-300 transition duration-200 mt-4"
            onClick={handleEditClick}
          >
            {isEditable ? 'Save Changes' : 'Edit Profile'}
          </button>
        </div>
      </div>
    </div>
  );
};

const InputField = ({ label, name, value, onChange, disabled }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700">{label}</label>
    <input
      type="text"
      name={name}
      value={value}
      onChange={onChange}
      disabled={disabled}
      className={`w-full px-3 py-2 border ${disabled ? 'bg-gray-200' : 'bg-white'} rounded text-black`}
    />
  </div>
);

const ReadOnlyField = ({ label, value }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700">{label}</label>
    <input
      type="text"
      value={value}
      disabled
      readOnly
      className="w-full px-3 py-2 border bg-gray-200 rounded text-black cursor-not-allowed"
    />
  </div>
);

export default UserProfileModal;
